import { z } from "zod";

const PROTOCOL_VERSION = 1;

const SessionContextSchema = z.object({
  repId: z.string().min(1).max(64),
  accountRef: z.string().min(1).max(64),
  tenantId: z.string().min(1).max(64),
  __synthetic: z.boolean().optional(),
  oneAgent: z
    .object({
      accountResourceName: z.string().min(1).max(512).optional(),
      customerResourceName: z.string().min(1).max(512).optional(),
    })
    .optional(),
});

export type SessionContext = z.infer<typeof SessionContextSchema>;

const HelloPayloadSchema = z.object({
  parentOrigin: z.string().url(),
  sessionContext: SessionContextSchema,
  deepLink: z.string().regex(/^#\//).optional(),
});

const HELLO_MESSAGE_TYPES = ["account-mgt:hello", "accountmgt:hello"] as const;
export type HelloMessageType = (typeof HELLO_MESSAGE_TYPES)[number];

const HelloSchema = z.object({
  version: z.literal(PROTOCOL_VERSION),
  type: z.enum(HELLO_MESSAGE_TYPES),
  payload: HelloPayloadSchema,
});

const ReadySchema = z.object({
  version: z.literal(PROTOCOL_VERSION),
  type: z.literal("account-mgt:ready"),
  payload: z.object({}).strict(),
});

const ResizeSchema = z.object({
  version: z.literal(PROTOCOL_VERSION),
  type: z.literal("account-mgt:resize"),
  payload: z.object({ heightPx: z.number().int().nonnegative() }),
});

export const InboundSchema = HelloSchema;
export type Inbound = z.infer<typeof InboundSchema>;

export const OutboundSchema = z.discriminatedUnion("type", [ReadySchema, ResizeSchema]);
export type Outbound = z.infer<typeof OutboundSchema>;

const STORAGE_KEY_LAST_PARENT = "__accountmgt_last_parent_origin";

function getAllowlist(): string[] {
  const raw = import.meta.env.VITE_PARENT_ORIGIN_ALLOWLIST ?? "";
  return raw
    .split(",")
    .map((o) => o.trim())
    .filter(Boolean);
}

export function isAllowedOrigin(origin: string): boolean {
  const allowlist = getAllowlist();
  if (allowlist.length === 0) return origin === window.location.origin;
  return allowlist.includes(origin);
}

export function isHelloMessageType(type: string): type is HelloMessageType {
  return (HELLO_MESSAGE_TYPES as readonly string[]).includes(type);
}

export function send(parent: Window, msg: Outbound, targetOrigin: string): void {
  if (targetOrigin === "*") {
    throw new Error('postMessage: refusing to send with targetOrigin "*"');
  }
  const parsed = OutboundSchema.safeParse(msg);
  if (!parsed.success) {
    console.warn("postMessage: dropping malformed outbound message", parsed.error);
    return;
  }
  parent.postMessage(parsed.data, targetOrigin);
}

export function listen(handler: (msg: Inbound, origin: string) => void): () => void {
  const onMessage = (event: MessageEvent): void => {
    if (!isAllowedOrigin(event.origin)) return;
    const parsed = InboundSchema.safeParse(event.data);
    if (!parsed.success) return;
    try {
      sessionStorage.setItem(STORAGE_KEY_LAST_PARENT, event.origin);
    } catch {
      // sessionStorage may be partitioned in iframe contexts.
    }
    handler(parsed.data, event.origin);
  };
  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
}

export function getLastParentOrigin(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY_LAST_PARENT);
  } catch {
    return null;
  }
}
